/**
 * How wide the page of music should be, in the engraver's tenths, to fill the desk.
 *
 * Verovio lays out to a page width given in tenths of a millimetre at scale 100, and draws it
 * at `width * scale / 100` pixels. Left at the A4 default the system is 798px wide whatever the
 * window is doing: on a narrow desk it is clipped, on a wide one the music huddles in the middle
 * with the rest of the paper blank. Neither is what an editor does -- a score fills the width it
 * has and reflows when the width or the zoom changes.
 *
 * So the width is measured, not assumed, and converted back into tenths at the current scale.
 * A `ResizeObserver` catches the window, the sidebar and the inspector opening and closing
 * alike, which a window `resize` listener would not.
 */

import { useEffect, useState, type RefObject } from "react";

/** A4 in tenths: what the engraver falls back to before anything has been measured. */
const A4_TENTHS = 2100;

/** Room for the padding and the scrollbar, so the last barline is never half off the edge. */
const MARGIN = 24;

export function useScoreWidth(board: RefObject<HTMLElement>, scale: number): number {
  const [pixels, setPixels] = useState(0);

  useEffect(() => {
    const element = board.current;
    if (element === null) return;
    const watch = new ResizeObserver((entries) => {
      const seen = entries[0]?.contentRect.width ?? 0;
      if (seen > 0) setPixels(seen);
    });
    watch.observe(element);
    setPixels(element.clientWidth);
    return () => {
      watch.disconnect();
    };
  }, [board]);

  if (pixels <= MARGIN) return A4_TENTHS;
  return Math.round(((pixels - MARGIN) * 100) / scale);
}
